import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useGetAppsQuery } from 'src/redux/services/apps';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import { ActivePlansResponse } from 'src/redux/services/plans';

interface Appkey {
  label: string;
  key: string;
}

export interface Props {
  selectedAppkey: Appkey | null;
  setSelectedAppkey: React.Dispatch<
    React.SetStateAction<{
      label: string;
      key: string;
    } | null>
  >;
  setSelectedJSKey?: React.Dispatch<React.SetStateAction<string>>;
  dataPlans?: ActivePlansResponse;
  skipCondition?: boolean;
  activeOnly?: boolean;
  sx?: React.CSSProperties;
}

const AppkeyAutocomplete: React.FC<Props> = ({ selectedAppkey, setSelectedAppkey, setSelectedJSKey, dataPlans, skipCondition, activeOnly, sx }) => {
  const [appkeyList, setAppkeyList] = React.useState<{ label: string; key: string }[]>([]);

  const {
    data: dataApps,
    isLoading: isLoadingApps,
    isError: isErrorApps,
  } = useGetAppsQuery({ max_items: 100, start: 0, search: null }, { skip: dataPlans !== undefined || skipCondition });

  // unique characters to wrap the app key
  const specialCharacterStart = ': (';
  const specialCharacterEnd = ')';

  React.useEffect(() => {
    if (dataApps?.items) {
      const dataAppsActive = activeOnly ? dataApps.items.filter((item) => item.active) : dataApps.items;
      const appkeyList = dataAppsActive.map((plan) => {
        return {
          label: `${plan.name}${specialCharacterStart}${plan.app_key}${specialCharacterEnd}`,
          key: plan.app_key,
        };
      });
      setAppkeyList(appkeyList);
      if (appkeyList[0]) setSelectedAppkey(appkeyList[0]);
      if (setSelectedJSKey) {
        const jsKeyListTemp = dataAppsActive.filter((item) => item.active).map((item) => item.js_app_key);
        if (jsKeyListTemp[0]) setSelectedJSKey(jsKeyListTemp[0]);
      }
    }
    if (dataPlans?.items) {
      const appkeyList = dataPlans.items.map((plan) => {
        return {
          label: `${plan.name}${specialCharacterStart}${plan.app_key}${specialCharacterEnd}`,
          key: plan.app_key,
        };
      });
      setAppkeyList(appkeyList);
      if (appkeyList[0]) setSelectedAppkey(appkeyList[0]);
    }
  }, [dataApps, dataPlans]);

  React.useEffect(() => {
    if (dataApps?.items) {
      const jsKeyListTemp = dataApps.items.filter((item) => item.active && item.app_key === selectedAppkey?.key).map((item) => item.js_app_key);
      if (setSelectedJSKey && jsKeyListTemp[0]) setSelectedJSKey(jsKeyListTemp[0]);
    }
  }, [selectedAppkey]);

  const handleChangeAppKey = (newAppKey: string) => {
    // dewrap the app key from the label
    const key = newAppKey.substring(
      newAppKey.indexOf(`${specialCharacterStart}`) + specialCharacterStart.length,
      newAppKey.length - specialCharacterEnd.length
    );

    const appKey = appkeyList.find((e) => e.key === key.trim());
    if (appKey) {
      setSelectedAppkey(appKey);
    }
  };
  return (
    <>
      {isLoadingApps && <LoadingScreen variant="circle" />}
      {!isLoadingApps && isErrorApps && <FailureScreen>An error occurred while fetching app keys, please retry</FailureScreen>}
      {!isLoadingApps && !isErrorApps && (dataApps || dataPlans) && (
        <Autocomplete
          disablePortal
          id="app-key-select"
          data-testid="app-key-select"
          options={appkeyList}
          autoHighlight
          value={selectedAppkey}
          sx={{ ...sx, marginTop: '16px' }}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onInputChange={(_, newInputValue) => {
            handleChangeAppKey(newInputValue);
          }}
          renderInput={(params) => <TextField {...params} label="App-key" fullWidth={true} />}
        />
      )}
    </>
  );
};

export default AppkeyAutocomplete;
